import { useCallback, useEffect, useRef, useState } from "react";
import ReaderToolbar, { type ReadingMode } from "./components/ReaderToolbar";
import ReaderCanvas from "./components/ReaderCanvas";
import ChapterDrawer from "./components/ChapterDrawer";
import { readerPages, chapters } from "@/mocks/readerPages";

export default function Reader() {
  const [mode, setMode] = useState<ReadingMode>("dual");
  const [night, setNight] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [chapterId, setChapterId] = useState(chapters[0].id);
  const [saved, setSaved] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const topRef = useRef<HTMLDivElement>(null);

  const chapter = chapters.find((c) => c.id === chapterId) ?? chapters[0];

  useEffect(() => {
    document.title = `${chapter.title} - ${"炎刃少年"} | MGB漫画网`;
  }, [chapter]);

  const handleScroll = useCallback(() => {
    const doc = document.documentElement;
    const max = doc.scrollHeight - doc.clientHeight;
    setScrollPct(max > 0 ? Math.min(100, (doc.scrollTop / max) * 100) : 0);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const markSaved = () => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  const goChapter = (id: number) => {
    setChapterId(id);
    setPageIndex(0);
    setDrawerOpen(false);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
    window.scrollTo({ top: 0 });
    markSaved();
  };

  const dualProgress = Math.round((pageIndex / (readerPages.length - 1)) * 100);
  const progress = mode === "scroll" ? Math.round(scrollPct) : dualProgress;

  const prevChapterId = chapters.find((c) => c.id < chapterId);
  const nextChapterId = chapters.find((c) => c.id > chapterId);
  const canPrevChapter = !!prevChapterId;
  const canNextChapter = !!nextChapterId;

  return (
    <div className={`min-h-screen ${night ? "bg-background-950 text-foreground-100" : "bg-background-50 text-foreground-950"}`}>
      <div ref={topRef}></div>

      <ReaderToolbar
        title="炎刃少年"
        chapterTitle={chapter.title}
        mode={mode}
        onModeChange={setMode}
        night={night}
        onToggleNight={() => setNight((v) => !v)}
        onOpenDir={() => setDrawerOpen(true)}
        saved={saved}
      />

      <ReaderCanvas
        pages={readerPages}
        mode={mode}
        pageIndex={pageIndex}
        night={night}
        onPrev={() => {
          setPageIndex((i) => Math.max(0, i - 2));
          markSaved();
        }}
        onNext={() => {
          setPageIndex((i) => Math.min(readerPages.length - 1, i + 2));
          markSaved();
        }}
        maxPage={readerPages.length - 1}
        onScrollModeProgress={setScrollPct}
      />

      {/* progress + chapter nav */}
      <div
        className={`sticky bottom-0 z-30 border-t px-4 py-3 md:px-6 ${
          night ? "border-background-800 bg-background-950/95" : "border-background-200 bg-background-50/95"
        }`}
      >
        <div
          className={`h-1.5 w-full overflow-hidden rounded-full ${
            night ? "bg-background-900" : "bg-background-200"
          }`}
        >
          <div
            className="h-full rounded-full bg-primary-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        <div className="mx-auto mt-3 flex max-w-5xl items-center justify-between gap-3">
          <button
            type="button"
            disabled={!canPrevChapter}
            onClick={() => prevChapterId && goChapter(prevChapterId.id)}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              night
                ? "bg-background-900 text-foreground-200 hover:bg-background-800"
                : "bg-background-100 text-foreground-700 hover:bg-primary-500 hover:text-background-50"
            }`}
          >
            <i className="ri-arrow-left-s-line text-base"></i>
            上一话
          </button>

          <div className="hidden flex-col items-center sm:flex">
            <span className="text-xs text-foreground-500">
              {chapter.title} · 已读 {progress}%
            </span>
            <span className={`mt-0.5 text-[11px] ${night ? "text-foreground-500" : "text-foreground-400"}`}>
              进度自动保存，下次打开接着看
            </span>
          </div>

          <button
            type="button"
            disabled={!canNextChapter}
            onClick={() => nextChapterId && goChapter(nextChapterId.id)}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              night
                ? "bg-background-900 text-foreground-200 hover:bg-background-800"
                : "bg-background-100 text-foreground-700 hover:bg-primary-500 hover:text-background-50"
            }`}
          >
            下一话
            <i className="ri-arrow-right-s-line text-base"></i>
          </button>
        </div>
      </div>

      <ChapterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        night={night}
        chapters={chapters}
        currentChapter={chapterId}
        onSelect={goChapter}
        comicTitle="炎刃少年"
      />
    </div>
  );
}